import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FaPlusCircle } from 'react-icons/fa';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { isValidEmail } from '../../utils/utils';

const SETTING_KEY = 'inquireToAddress';

/**
 * マスタメンテナンス - お問い合わせ設定 画面
 */
const InquireSetting: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);
  const TITLE = t('features.MasterMaintenance.InquireSetting.title');

  const [emails, setEmails] = useState<string[]>(['']);
  const [emailsError, setEmailsError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchToAddress();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchToAddress = async () => {
    try {
      const response = await WebAPI.request({
        query: `
          query getSettingValueByKey($settingKey: String, $hasDeleted: Boolean) {
            getSettingValueByKey(settingKey: $settingKey, hasDeleted: $hasDeleted) {
              settingValue
            }
          }`,
        variables: { settingKey: SETTING_KEY, hasDeleted: false },
      });
      const value = response?.data?.getSettingValueByKey?.settingValue ?? '';
      setEmails(value ? value.split(',').map((e: string) => e.trim()) : ['']);
    } catch (err) {
      console.error('fetchToAddress failed:', err);
      toast({
        title: TITLE,
        description: t('common.message.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const validate = (): boolean => {
    const nonEmpty = emails.map(e => e.trim()).filter(e => e.length > 0);
    if (nonEmpty.length === 0) {
      setEmailsError(t('features.MasterMaintenance.InquireSetting.validation.toAddressRequired'));
      return false;
    }
    if (!nonEmpty.every(e => isValidEmail(e))) {
      setEmailsError(t('features.MasterMaintenance.InquireSetting.validation.toAddressInvalid'));
      return false;
    }
    setEmailsError('');
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    const settingValue = emails.map(e => e.trim()).filter(e => e.length > 0).join(',');
    try {
      const response = await WebAPI.request({
        query: `
          mutation putSettingValueByKey($input: PutSettingValueByKeyInput!) {
            putSettingValueByKey(input: $input) {
              status
              message
            }
          }`,
        variables: { input: { settingKey: SETTING_KEY, settingValue } },
      });

      if (response?.data?.putSettingValueByKey?.status === 'success') {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.InquireSetting.message.settingSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box p={6} maxW="600px">
      <VStack align="stretch" spacing={6}>
        <FormControl isInvalid={!!emailsError}>
          <FormLabel fontWeight="bold">
            {t('features.MasterMaintenance.InquireSetting.label.toAddress')}
          </FormLabel>
          <VStack align="stretch" spacing={2}>
            {emails.map((email, idx) => (
              <InputGroup key={idx}>
                <Input
                  type="text"
                  value={email}
                  onChange={e => {
                    setEmails(prev => prev.map((v, i) => i === idx ? e.target.value : v));
                    setEmailsError('');
                  }}
                  placeholder={t('features.MasterMaintenance.InquireSetting.placeholder.toAddress')}
                  maxLength={500}
                  bgColor="white"
                  pr={emails.length > 1 ? '40px' : undefined}
                />
                {emails.length > 1 && (
                  <InputRightElement height="100%">
                    <IconButton
                      aria-label="Delete email"
                      icon={<RiDeleteBin6Fill />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => {
                        setEmails(prev => prev.filter((_, i) => i !== idx));
                        setEmailsError('');
                      }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            ))}
          </VStack>
          <IconButton
            aria-label="Add email"
            icon={<FaPlusCircle />}
            onClick={() => setEmails(prev => [...prev, ''])}
            colorScheme="blue"
            size="sm"
            mt={2}
          />
          <FormErrorMessage>{emailsError}</FormErrorMessage>
        </FormControl>

        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={isSaving}
          alignSelf="flex-start"
        >
          {t('common.button.save')}
        </Button>
      </VStack>
    </Box>
  );
};

export default InquireSetting;
